import { ApolloError } from "@apollo/client";
import { logger } from "@clockwise/client-commons/src/util/logger";
import { TrackingEvents, useTracking } from "@clockwise/web-commons/src/util/analytics.util";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import React from "react";
import toast from "react-hot-toast";
import { useProcessMessage } from "../chat/ai-chat/hooks/useProcessMessage";
import { generateRescheduleCQL } from "../event-card/utils/generateCQL";
import { useRescheduleEvent } from "../hooks/useRescheduleEvent";
import { useRescheduleOptions } from "../hooks/useRescheduleOptions";
import { QuickReschedule, RescheduleState } from "./QuickReschedule";
import { getLabelTextFromISO } from "./util/getLabelTextFromISO";

type Props = {
  emphasis?: boolean;
  eventStartTime: string;
  eventTitle: string;
  externalEventId: string;
  calendarId: string;
  onHover?: (startISO: string | null) => void;
  onRedirect?: () => void;
};

export const QuickRescheduleControler = ({
  emphasis = false,
  eventStartTime,
  eventTitle,
  externalEventId,
  calendarId,
  onHover,
  onRedirect,
}: Props) => {
  const [rescheduleState, setRescheduleState] = React.useState<RescheduleState>("default");
  const [selectedStartISO, setSelectedStartISO] = React.useState<string | null>(null);
  const [confirmationMessage, setConfirmationMessage] = React.useState("");
  const { processMessage, canProcessMessage } = useProcessMessage();
  const track = useTracking();

  const onOptionsError = (error: ApolloError) => {
    logger.error("Failed to fetch reschedule options for event", error);
  };

  const { error: errorOptions, loading: loadingOptions, rescheduleOptions } = useRescheduleOptions({
    externalEventId,
    calendarId,
    onError: onOptionsError,
  });

  const onReschduleCompleted = () => {
    if (errorReschedule) {
      setConfirmationMessage("");
    } else {
      // slight delay to allow calendar to catch up
      setTimeout(() => {
        setConfirmationMessage(
          `Your event has been rescheduled to ${
            selectedStartISO ? getLabelTextFromISO(selectedStartISO) : "a new time"
          }.`,
        );
        setRescheduleState("confirmed");
      }, 250);
    }
  };

  const onRescheduleError = (error: ApolloError) => {
    logger.error("Failed to reschedule event", error);
  };

  const { rescheduleEvent, error: errorReschedule } = useRescheduleEvent(
    onReschduleCompleted,
    onRescheduleError,
  );

  const options = rescheduleOptions?.map((option) => option.start) || [];

  const handleHover = (startISO: string | null) => {
    onHover?.(selectedStartISO || startISO);
  };

  const handleSelect = (startISO: string) => {
    setRescheduleState("selected");
    setSelectedStartISO(startISO);
    onHover?.(startISO);
    track(TrackingEvents.QUICK_RESCHEDULE.SELECTED);
  };

  const handleConfirm = () => {
    if (!selectedStartISO) return;

    setRescheduleState("confirming");
    rescheduleEvent(externalEventId, selectedStartISO, getRenderTimeZone());
    onHover?.(null);
    track(TrackingEvents.QUICK_RESCHEDULE.CONFIRMED);
  };

  const handleFindMoreTimes = () => {
    preProcessMessage();
    track(TrackingEvents.QUICK_RESCHEDULE.FIND_MORE_TIMES, { canProcessMessage });
    toast.success(
      canProcessMessage
        ? "Finding more times with Clockwise"
        : "Try Clockwise AI to find more times",
    );
    onRedirect?.();
  };

  const preProcessMessage = () => {
    void processMessage(
      `Reschedule ${eventTitle}`,
      {
        eventMentions: [
          {
            title: eventTitle,
            externalEventId,
            startTime: eventStartTime,
          },
        ],
        personMentions: [],
      },
      generateRescheduleCQL(externalEventId),
    );
  };

  let errorMessage = "";
  if (errorReschedule) {
    errorMessage = "Unable to update your meeting.";
    console.error(errorReschedule.message);
  } else if (errorOptions) {
    errorMessage = "Unable to find quick reschedule times.";
    console.error(errorOptions.message);
  }

  return (
    <QuickReschedule
      confirmationMessage={confirmationMessage}
      emphasis={!loadingOptions && emphasis}
      errorMessage={errorMessage}
      loading={loadingOptions}
      onConfirm={handleConfirm}
      onFindMoreTimes={handleFindMoreTimes}
      onHover={handleHover}
      onSelect={handleSelect}
      options={options}
      state={rescheduleState}
      value={selectedStartISO}
    />
  );
};
